<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import clsx from 'clsx';

type ButtonColor =
  | 'dark/zinc'
  | 'light'
  | 'dark/white'
  | 'dark'
  | 'white'
  | 'zinc'
  | 'indigo'
  | 'red'
  | 'blue'
  | 'green';

const props = withDefaults(
  defineProps<{
    href?: string;
    type?: 'button' | 'submit' | 'reset';
    color?: ButtonColor;
    outline?: boolean;
    plain?: boolean;
    disabled?: boolean;
    class?: string;
  }>(),
  { type: 'button' },
);

const base = [
  'relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-lg border text-base/6 font-semibold',
  'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 focus:outline-hidden',
  'disabled:opacity-50 disabled:pointer-events-none',
  'cursor-pointer',
];

const outlineStyle = [
  'border-zinc-950/10 text-zinc-950 hover:bg-zinc-950/[2.5%] active:bg-zinc-950/[2.5%]',
  'dark:border-white/15 dark:text-white dark:hover:bg-white/5 dark:active:bg-white/5',
];

const plainStyle = [
  'border-transparent text-zinc-950 hover:bg-zinc-950/5 active:bg-zinc-950/5',
  'dark:text-white dark:hover:bg-white/10 dark:active:bg-white/10',
];

const solidShell = [
  'border-transparent text-white shadow-sm',
  'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)]',
  'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',
];

const colorMap: Record<ButtonColor, string[]> = {
  'dark/zinc': [
    'bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-700',
    'dark:bg-zinc-700 dark:hover:bg-zinc-600',
  ],
  light: [
    'bg-white text-zinc-950 border-zinc-950/10 hover:bg-zinc-50 active:bg-zinc-100',
    'dark:bg-zinc-800 dark:text-white dark:hover:bg-zinc-700',
  ],
  'dark/white': [
    'bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-700',
    'dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100',
  ],
  dark: ['bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-700'],
  white: [
    'bg-white text-zinc-950 border-zinc-950/10 hover:bg-zinc-50 active:bg-zinc-100',
  ],
  zinc: ['bg-zinc-600 hover:bg-zinc-500 active:bg-zinc-700'],
  indigo: ['bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700'],
  red: ['bg-red-600 hover:bg-red-500 active:bg-red-700'],
  blue: ['bg-blue-600 hover:bg-blue-500 active:bg-blue-700'],
  green: ['bg-green-600 hover:bg-green-500 active:bg-green-700'],
};

const classes = computed(() => {
  if (props.outline) return clsx(base, outlineStyle, props.class);
  if (props.plain) return clsx(base, plainStyle, props.class);
  return clsx(base, solidShell, colorMap[props.color ?? 'dark/zinc'], props.class);
});

const isExternal = computed(
  () =>
    typeof props.href === 'string' &&
    (/^https?:\/\//i.test(props.href) || props.href.startsWith('mailto:')),
);
</script>

<template>
  <!-- A disabled link is rendered as an inert span so it can't be clicked or
       followed by keyboard/router navigation, unlike an <a>/RouterLink which
       ignore the disabled attribute. -->
  <span
    v-if="href && disabled"
    :class="[classes, 'pointer-events-none opacity-50']"
    aria-disabled="true"
  >
    <slot />
  </span>
  <RouterLink
    v-else-if="href && !isExternal"
    :to="href"
    :class="classes"
  >
    <slot />
  </RouterLink>
  <a
    v-else-if="href && isExternal"
    :href="href"
    :class="classes"
    target="_blank"
    rel="noopener noreferrer"
  >
    <slot />
  </a>
  <button
    v-else
    :type="type"
    :class="classes"
    :disabled="disabled"
  >
    <slot />
  </button>
</template>
